import { useCallback, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { taskApi } from '../../api/task.api';
import { BASE_MEDIA_URL } from '../../api/client';
import { useUI } from '../../store/UIContext';
import { Badge, Btn, Card, EmptyState, PageHeader, Spinner, Textarea } from '../../components/ui/index';
import { deadlineCountdown, formatTaskDate, taskStatusInfo } from '../../utils/taskHelpers';

const Row = ({ label, children }) => (
  <div style={{ display: 'flex', gap: 10, fontSize: 13, padding: '6px 0' }}>
    <span style={{ color: '#64748b', minWidth: 110 }}>{label}</span>
    <span style={{ color: '#0f172a', flex: 1 }}>{children}</span>
  </div>
);

export default function TaskDetailPage() {
  const { id } = useParams();
  const { addToast } = useUI();
  const [task, setTask] = useState(null);
  const [missing, setMissing] = useState(false);
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const res = await taskApi.get(id);
      setTask(res.data);
    } catch { setMissing(true); }
  }, [id]);
  useEffect(() => { load(); }, [load]);

  const act = async (fn, okMsg) => {
    setBusy(true);
    try {
      await fn();
      addToast(okMsg, 'success');
      setNote('');
      await load();
    } catch (e) {
      addToast(typeof e?.response?.data === 'string' ? e.response.data : 'خطا در ثبت', 'error');
    }
    setBusy(false);
  };

  if (missing) return <EmptyState title="تسک یافت نشد" />;
  if (!task) return <div style={{ display: 'flex', justifyContent: 'center', padding: 48 }}><Spinner /></div>;

  const isAssignee = !!task.viewer_is_assignee;
  const isCreator = !!task.viewer_is_creator;
  const info = taskStatusInfo(task);
  const active = task.status === 'open' || task.status === 'responded';

  return (
    <div className="fade-in" style={{ maxWidth: 760 }}>
      <PageHeader
        title={task.title}
        action={<Link to={isCreator ? '/tasks?scope=created' : '/tasks'} style={{ fontSize: 13, color: '#64748b' }}>بازگشت</Link>}
      />
      <Card style={{ padding: 20, marginBottom: 16 }}>
        <Row label="وضعیت"><Badge variant={info.variant}>{info.label}</Badge></Row>
        <Row label="تعریف‌کننده">{task.creator_name}</Row>
        <Row label="محول به">{task.assignee_name}</Row>
        <Row label="تاریخ تعریف">{formatTaskDate(task.sent_at)}</Row>
        <Row label="ددلاین">
          {formatTaskDate(task.deadline)}
          {active && <span style={{ marginRight: 8, color: task.overdue ? '#b91c1c' : '#475569', fontWeight: 600 }}>({deadlineCountdown(task.deadline)})</span>}
        </Row>
        {task.responded_at && <Row label="زمان پاسخ">{formatTaskDate(task.responded_at)}</Row>}
        {task.approved_at && <Row label="زمان تایید">{formatTaskDate(task.approved_at)}</Row>}
        {task.status === 'approved' && task.overdue && <Row label="توضیح"><span style={{ color: '#b91c1c' }}>با تاخیر انجام شده</span></Row>}
        <div style={{ whiteSpace: 'pre-wrap', fontSize: 14, color: '#334155', marginTop: 12, lineHeight: 1.9 }}>{task.description}</div>
        {task.files_list?.length > 0 && (
          <div style={{ marginTop: 14, display: 'flex', flexDirection: 'column', gap: 6 }}>
            {task.files_list.map((f) => (
              <a key={f.server_file_name} href={BASE_MEDIA_URL + f.server_file_name} target="_blank" rel="noreferrer"
                style={{ fontSize: 13, color: '#1d4ed8' }}>📎 {f.main_file_name}</a>
            ))}
          </div>
        )}
      </Card>

      {task.review_note && task.status === 'open' && (
        <Card style={{ padding: 16, marginBottom: 16, borderColor: '#fed7aa' }}>
          <div style={{ fontWeight: 700, fontSize: 13, marginBottom: 4 }}>تسک توسط مدیر بازگردانده شد</div>
          <div style={{ fontSize: 13, color: '#334155' }}>{task.review_note}</div>
        </Card>
      )}

      {task.response_result && task.status !== 'open' && (
        <Card style={{ padding: 16, marginBottom: 16 }}>
          <div style={{ fontWeight: 700, fontSize: 13, marginBottom: 4 }}>
            پاسخ کارمند: {task.response_result === 'done' ? 'انجام شد' : 'انجام نشد'}
          </div>
          {task.response_note && <div style={{ fontSize: 13, color: '#334155' }}>{task.response_note}</div>}
          {task.review_note && task.status === 'approved' && (
            <div style={{ fontSize: 12, color: '#64748b', marginTop: 8 }}>یادداشت مدیر: {task.review_note}</div>
          )}
        </Card>
      )}

      {isAssignee && task.status === 'open' && (
        <Card style={{ padding: 16 }}>
          <Textarea label="توضیحات (برای «انجام نشد» الزامی است)" value={note} rows={3} maxLength={1000}
            onChange={(e) => setNote(e.target.value)} />
          <div style={{ display: 'flex', gap: 10, marginTop: 12 }}>
            <Btn variant="success" disabled={busy}
              onClick={() => act(() => taskApi.respond({ task_id: task.task_id, result: 'done', note }), 'پاسخ شما ثبت شد')}>
              انجام دادم
            </Btn>
            <Btn variant="danger" disabled={busy}
              onClick={() => act(() => taskApi.respond({ task_id: task.task_id, result: 'not_done', note }), 'پاسخ شما ثبت شد')}>
              انجام ندادم
            </Btn>
          </div>
        </Card>
      )}

      {isCreator && task.status === 'responded' && (
        <Card style={{ padding: 16 }}>
          <Textarea label="یادداشت مدیر (برای بازگرداندن الزامی است)" value={note} rows={3} maxLength={1000}
            onChange={(e) => setNote(e.target.value)} />
          <div style={{ display: 'flex', gap: 10, marginTop: 12 }}>
            {task.response_result === 'done' && (
              <Btn variant="success" disabled={busy}
                onClick={() => act(() => taskApi.review({ task_id: task.task_id, action: 'approve', note }), 'تسک تایید شد')}>
                تایید انجام
              </Btn>
            )}
            <Btn variant="danger" disabled={busy}
              onClick={() => act(() => taskApi.review({ task_id: task.task_id, action: 'reject', note }), 'تسک بازگردانده شد')}>
              بازگرداندن برای انجام مجدد
            </Btn>
          </div>
        </Card>
      )}
    </div>
  );
}
